/**
 * Chủ đề sáng/tối/tự động — FR-004, mở rộng ở OQ-288, đổi cách "tự động" ở OQ-336.
 *
 * Ghi nhớ bằng localStorage; cùng cơ chế `data-theme` trên `<html>` như prototype.
 *
 * ── Hai khái niệm, đừng trộn ───────────────────────────────────────────────
 *
 *   `mode`  — thứ người dùng CHỌN: `light` · `dark` · `auto`
 *   `theme` — thứ đang HIỂN THỊ:   `light` · `dark`
 *
 * ── OQ-336: "tự động" nghĩa là theo GIỜ, không theo hệ điều hành ───────────
 *
 * **Chủ dự án**: *"chế độ tự động có vẻ lỗi hoặc không lấy theo múi giờ cài đặt
 * trong hồ sơ nên bây giờ là 9 giờ tối nhưng giao diện vẫn sáng"*.
 *
 * Bản trước suy từ `prefers-color-scheme` — tức là **cài đặt của Windows/macOS**,
 * không phải giờ trong ngày. Nó chạy đúng như thiết kế, nhưng thiết kế ấy không
 * phải thứ người dùng chờ đợi: máy để sáng cả ngày thì 9 giờ tối giao diện vẫn
 * trắng.
 *
 * Nay: **18h–6h là tối**, tính theo múi giờ trong hồ sơ người dùng.
 *
 * ── Vì sao vẫn phải kiểm lại theo thời gian ────────────────────────────────
 *
 * Tính một lần lúc mở trang thì tab để mở qua 18h sẽ ở nguyên màu sáng — đúng lỗi
 * mà OQ-288 đã sửa cho `prefers-color-scheme`, nay lặp lại ở dạng khác. Nên có
 * một nhịp kiểm mỗi phút.
 *
 * Mỗi phút chứ không phải mỗi giây: sai số tối đa 60 giây ở một ranh giới mà
 * không ai canh đồng hồ, đổi lấy việc không đánh thức trình duyệt 86.400 lần một
 * ngày.
 */

import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/** Màu đang hiển thị. */
export type ThemeName = 'light' | 'dark';
/** Lựa chọn của người dùng. */
export type ThemeMode = 'light' | 'dark' | 'auto';

const STORAGE_KEY = 'dnshub.theme';

/** Ba chế độ, đúng thứ tự hiển thị trong menu. */
export const THEME_MODES: ThemeMode[] = ['light', 'dark', 'auto'];

/**
 * Khung giờ tối: từ 18h tới trước 6h — chủ dự án chốt.
 *
 * Để lộ ra thành hằng số có tên thay vì hai con số nằm giữa một biểu thức: đây là
 * quyết định sản phẩm, không phải chi tiết cài đặt, và người sau đọc `>= 18` giữa
 * dòng sẽ không biết nó từ đâu ra.
 */
export const GIO_BAT_DAU_TOI = 18;
export const GIO_KET_THUC_TOI = 6;

/**
 * Giờ hiện tại (0–23) ở một múi giờ.
 *
 * Dùng `Intl` chứ không cộng trừ độ lệch bằng tay: múi giờ có giờ mùa hè, và một
 * phép cộng cứng sẽ sai đúng hai lần mỗi năm ở những nơi còn dùng nó.
 *
 * Múi giờ sai hoặc thiếu ⇒ rơi về giờ máy. Không ném: chọn nhầm màu giao diện là
 * phiền, còn một ngoại lệ ở đây làm trắng cả trang.
 */
export function gioTaiMuiGio(muiGio?: string, luc: Date = new Date()): number {
  if (muiGio) {
    try {
      const s = new Intl.DateTimeFormat('en-US', {
        timeZone: muiGio,
        hour: 'numeric',
        hour12: false,
      }).format(luc);
      const n = Number(s);
      if (Number.isInteger(n) && n >= 0 && n <= 23) return n;
    } catch {
      /* Múi giờ không hợp lệ — dùng giờ máy. */
    }
  }
  return luc.getHours();
}

/** Màu theo giờ trong ngày — trái tim của chế độ "tự động". */
export function themeTheoGio(muiGio?: string, luc: Date = new Date()): ThemeName {
  const gio = gioTaiMuiGio(muiGio, luc);
  return gio >= GIO_BAT_DAU_TOI || gio < GIO_KET_THUC_TOI ? 'dark' : 'light';
}

/**
 * Chế độ đã lưu.
 *
 * Chịu cả giá trị cũ (`'light'`/`'dark'`) lẫn mới (`'auto'`): người dùng đã dùng
 * bản trước có sẵn một trong hai chuỗi ấy trong localStorage, và đọc không ra thì
 * họ mất lựa chọn của mình mà không hiểu vì sao.
 */
export function readThemeMode(): ThemeMode {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'dark' || stored === 'light' || stored === 'auto') return stored;
  } catch {
    /* localStorage bị chặn */
  }
  return 'auto';
}

/**
 * Màu cần áp NGAY lúc khởi động — `main.tsx` gọi trước khi React dựng cây.
 *
 * Chưa biết múi giờ hồ sơ ở thời điểm này (người dùng còn chưa nạp), nên dùng giờ
 * máy. Nếu hồ sơ đặt múi giờ khác, `ThemeProvider` chỉnh lại ngay sau đó — sai
 * lệch nếu có chỉ kéo dài một nhịp vẽ.
 */
export function readInitialTheme(): ThemeName {
  const mode = readThemeMode();
  return mode === 'auto' ? themeTheoGio() : mode;
}

interface ThemeValue {
  /** Màu đang hiển thị. */
  theme: ThemeName;
  /** Lựa chọn của người dùng. */
  mode: ThemeMode;
  setMode: (next: ThemeMode) => void;
  /**
   * Múi giờ dùng cho chế độ tự động.
   *
   * `ThemeProvider` nằm NGOÀI `AuthProvider` (nó phải bọc cả màn đăng nhập), nên
   * không tự đọc được hồ sơ. Ai biết múi giờ thì đẩy vào — hiện là `AppShell`.
   */
  datMuiGio: (tz: string | undefined) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readThemeMode);
  const [muiGio, setMuiGio] = useState<string | undefined>(undefined);
  const [nhip, setNhip] = useState(0);

  /*
   * Nhịp kiểm mỗi phút — xem lý do ở đầu tệp.
   *
   * Chạy LUÔN LUÔN, không chỉ khi `mode === 'auto'`: đăng ký theo `mode` tạo ra
   * một khoảng mù khi người dùng vừa chuyển về `auto` giữa hai nhịp.
   */
  useEffect(() => {
    const id = setInterval(() => setNhip((n) => n + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  const theme: ThemeName = useMemo(() => {
    void nhip; // nhịp chỉ để tính lại; giá trị của nó không dùng tới
    return mode === 'auto' ? themeTheoGio(muiGio) : mode;
  }, [mode, muiGio, nhip]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useEffect(() => {
    try {
      /* Lưu MODE, không lưu theme: lưu màu đang hiện thì `auto` biến thành một
         lựa chọn cứng ngay ở lần tải trang sau. */
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      /* bỏ qua */
    }
  }, [mode]);

  const setMode = useCallback((next: ThemeMode) => setModeState(next), []);
  const datMuiGio = useCallback((tz: string | undefined) => setMuiGio(tz), []);

  const value = useMemo<ThemeValue>(
    () => ({ theme, mode, setMode, datMuiGio }),
    [theme, mode, setMode, datMuiGio],
  );
  return createElement(ThemeContext.Provider, { value }, children);
}

export function useTheme(): ThemeValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme phải nằm trong <ThemeProvider>');
  return ctx;
}
